import { COMMA, ENTER, SPACE, TAB } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Optional, Self } from '@angular/core';
import { AbstractControl, FormControl, NgControl, Validators } from '@angular/forms';
import { MAT_CHIPS_DEFAULT_OPTIONS, MatChipInputEvent } from '@angular/material/chips';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { SimpleField } from '../../abstracts/simple-field';

@Component({
  selector: 'fb-email-list-field',
  templateUrl: './email-list-field.component.html',
  styles: [
    'input.mat-chip-input { width: 100%; }',
    '.email-mat-chip { min-width: 0 }',
    '.email-ellipsis { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }',
  ],
  providers: [
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {
        separatorKeyCodes: [ENTER],
      },
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailListFieldComponent extends SimpleField<string[]> implements OnInit {
  emailControl: FormControl = new FormControl('', [Validators.email]);
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];
  addOnBlur: boolean = false;
  emails: string[] = [];

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional()
    @Self()
    protected override ngControl: NgControl,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.emailControl.valueChanges.pipe(untilComponentDestroyed(this)).subscribe((inputValue: string) => {
      const control: AbstractControl = this.ngControl.control;
      if (this.emailControl.valid && inputValue) {
        control.setErrors(null);
        control.markAsDirty();
        this.value = [...this.emails, inputValue];
        return;
      }

      control.setErrors(this.emailControl.errors);
    });
  }

  remove(email: string): void {
    const emails: string[] = this.emails;
    const index: number = emails.indexOf(email);
    if (index >= 0) {
      emails.splice(index, 1);
    }

    this.emails = [...emails];
    this.value = [...emails];
  }

  add(event: Partial<MatChipInputEvent>): void {
    const input: HTMLInputElement = event.input;
    const value: string = (event.value || '').trim();

    if (!value || !this.emailControl.valid || this.emails.length > 15 || this.emails.includes(value)) {
      return;
    }

    if (input) {
      input.value = '';
    }

    this.emailControl.setValue(null, { emitEvent: false });
    this.emails = [...this.emails, value];
  }

  onKeyDown($event: KeyboardEvent): void {
    if ($event.keyCode === TAB) {
      const input: HTMLInputElement = $event.target as HTMLInputElement;
      const value: string = input.value;
      if (!value) {
        return;
      }

      $event.preventDefault();
      if (this.emailControl.valid) {
        this.add({ input, value });
        return;
      }
    }
  }

  protected override onWriteValue(value: string[]): void {
    this.emails = value || [];
    if (this.emailControl.value) {
      this.emailControl.setValue(null, { emitEvent: false });
    }
  }
}
